import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

const navItems = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "综艺", to: "/category/variety" },
];

export default function Navbar() {
  const navigate = useNavigate();
  const location = useLocation();
  const [keyword, setKeyword] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const kw = keyword.trim();
    if (!kw) return;
    navigate(`/search?q=${encodeURIComponent(kw)}`);
    setMenuOpen(false);
  };

  const isActive = (to: string) => {
    if (to === "/") return location.pathname === "/";
    if (to.startsWith("/category")) {
      return location.pathname.startsWith("/category") && location.pathname.includes(to.split("/")[2]);
    }
    return location.pathname === to;
  };

  return (
    <header
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur border-b border-background-200"
          : "bg-background-50 border-b border-background-100"
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex h-16 md:h-[72px] items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer" aria-label="蘑菇视频首页">
            <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-background-50 text-xl">
              <i className="ri-play-fill" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span className="font-heading text-lg md:text-xl font-bold tracking-tight text-foreground-950">
                蘑菇视频
              </span>
              <span className="hidden sm:block text-[11px] text-foreground-500 tracking-wide">MOGU VIDEO</span>
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-1" aria-label="主导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`relative px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                  isActive(item.to)
                    ? "text-primary-600"
                    : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
                }`}
              >
                {item.label}
                {isActive(item.to) && (
                  <span className="absolute left-1/2 -translate-x-1/2 bottom-0.5 h-0.5 w-5 rounded-full bg-primary-500"></span>
                )}
              </Link>
            ))}
            <Link
              to="/about"
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors cursor-pointer ${
                location.pathname === "/about"
                  ? "text-primary-600"
                  : "text-foreground-700 hover:text-foreground-950 hover:bg-background-100"
              }`}
            >
              关于我们
            </Link>
          </nav>

          <div className="flex items-center gap-2 md:gap-3">
            <form onSubmit={handleSearch} className="hidden md:flex items-center relative" role="search">
              <span className="absolute left-3 flex h-4 w-4 items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base" aria-hidden="true"></i>
              </span>
              <input
                type="search"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索影视剧、演员…"
                aria-label="搜索影视内容"
                className="w-40 lg:w-56 xl:w-64 rounded-full border border-background-200 bg-background-100 py-2 pl-9 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none transition-all focus:border-primary-400 focus:bg-background-50 focus:w-56 lg:focus:w-72"
              />
            </form>

            <Link
              to="/search"
              className="md:hidden flex h-10 w-10 items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 cursor-pointer"
              aria-label="搜索"
            >
              <i className="ri-search-line text-xl" aria-hidden="true"></i>
            </Link>

            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 cursor-pointer"
              aria-label="打开菜单"
              aria-expanded={menuOpen}
            >
              <i className={menuOpen ? "ri-close-line text-2xl" : "ri-menu-line text-2xl"} aria-hidden="true"></i>
            </button>
          </div>
        </div>
      </div>

      {menuOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 pb-5 pt-4">
          <form onSubmit={handleSearch} className="flex items-center relative mb-4" role="search">
            <span className="absolute left-3 flex h-4 w-4 items-center justify-center text-foreground-400">
              <i className="ri-search-line text-base" aria-hidden="true"></i>
            </span>
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索影视剧、演员…"
              aria-label="搜索影视内容"
              className="w-full rounded-full border border-background-200 bg-background-100 py-2.5 pl-9 pr-4 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50"
            />
          </form>
          <nav className="flex flex-col gap-1" aria-label="移动端导航">
            {navItems.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium cursor-pointer ${
                  isActive(item.to) ? "bg-primary-50 text-primary-700" : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/about"
              className={`flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium cursor-pointer ${
                location.pathname === "/about"
                  ? "bg-primary-50 text-primary-700"
                  : "text-foreground-700 hover:bg-background-100"
              }`}
            >
              关于我们
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}